import { truncateText } from '@ardoq/graph-labeling';
import type { MeasureLabel } from './standard/toLeafText';

type WrapWordsArgs = {
  text: string;
  availableWidth: number;
  measure: MeasureLabel;
  maxLines: number;
};

/**
 * `text` broken across at most `maxLines`, at spaces only, elided where it does not fit.
 *
 * `wrapString` hyphenates a word wider than the line, which suits prose and not component names:
 * in a circle a few characters across it returns `Em-` over `plo-` over `ye…`, and a name broken
 * at an arbitrary letter is harder to recognise than the same name cut short.
 */
export const wrapWords = ({
  text,
  availableWidth,
  measure,
  maxLines,
}: WrapWordsArgs): string[] => {
  const truncate = truncateText(measure);
  const words = text.split(/\s+/).filter(word => word.length > 0);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const proposal = current ? `${current} ${word}` : word;
    if (measure(proposal) <= availableWidth) {
      current = proposal;
      continue;
    }
    /* The line the rest has to be squeezed into, so the ellipsis stands for every word left
       rather than only this one. */
    if (lines.length + 1 === maxLines) {
      return [...lines, truncate(proposal, availableWidth)];
    }
    if (current) {
      lines.push(current);
      current = '';
    }
    if (measure(word) <= availableWidth) {
      current = word;
      continue;
    }
    /* Asked again because pushing `current` just spent a line. Without this the truncated
       word takes one more, and a caller that sized a circle or a heading band for `maxLines`
       gets a block taller than the space it measured. */
    if (lines.length + 1 === maxLines) {
      return [...lines, truncate(word, availableWidth)];
    }
    lines.push(truncate(word, availableWidth));
  }

  return current ? [...lines, current] : lines;
};
